import React, { useEffect, useState } from "react";
import { TextInput } from "../addressFormInput/textInput";
import { FocusAware } from "../focusAware";
import styles from "./comboBox.css";
import { config } from "@fidget/common/config/config";

const MIN_PROVIDER_QUERY_LENGTH = 3 as const;

interface Props<T> {
  itemsProvider: (query: string) => Promise<T[]> | T[];
  itemToString: (item: T) => string;
  keyExtractor: (item: T) => string;
  disabled?: boolean;
  label?: string;
  onQueryChange?: (query: string) => void;
  onItemPick?: (item: T) => void;
  minProviderQueryLength?: number;
  providerTimeout?: number;
}

export const ComboBox = <T extends {}>(props: Props<T>) => {
  const {
    providerTimeout = config.services.times.inputDebounceMs,
    minProviderQueryLength = MIN_PROVIDER_QUERY_LENGTH
  } = props;
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<T[]>([]);
  const [isShowingOptions, setIsShowingOptions] = useState(false);

  const findQuery = (item: T) => props.itemToString(item) === query;

  const handleQueryChange = (nextQuery: string) => {
    setQuery(nextQuery);
    props.onQueryChange?.(nextQuery);
  };

  const handleItemPick = (item: T) => {
    setIsShowingOptions(false);
    const stringifiedItem = props.itemToString(item);
    handleQueryChange(stringifiedItem);
    setOptions(options.filter((option) => props.itemToString(option).includes(stringifiedItem)));
    props.onItemPick?.(item);
  };

  useEffect(() => {
    if (!isShowingOptions) {
      return;
    }
    if (query.length < minProviderQueryLength) {
      return setOptions([]);
    }
    const timeout = setTimeout(async () => {
      const providedItems = await props.itemsProvider(query);
      setOptions(providedItems);
      const foundItem = providedItems.find(findQuery);
      if (foundItem) {
        props.onItemPick?.(foundItem);
      }
    }, providerTimeout);
    return () => clearTimeout(timeout);
  }, [query, minProviderQueryLength, providerTimeout, isShowingOptions]);

  const handleOptionKeyDown = (e: React.KeyboardEvent<HTMLLIElement>, item: T) => {
    if (e.key === "Enter") {
      handleItemPick(item);
    }
  };

  const renderOption = (item: T) => (
    <li
      key={props.keyExtractor(item)}
      onClick={() => handleItemPick(item)}
      tabIndex={0}
      className={styles.option}
      onKeyDown={(e) => handleOptionKeyDown(e, item)}
    >
      {props.itemToString(item)}
    </li>
  );

  const renderOptions = () => {
    const filteredOptions = options.filter((item) => !findQuery(item));
    return (
      !!filteredOptions.length && (
        <ul className={styles.optionsContainer}>{filteredOptions.map(renderOption)}</ul>
      )
    );
  };

  return (
    <FocusAware
      onFocus={() => setIsShowingOptions(true)}
      onBlur={() => setIsShowingOptions(false)}
      className={styles.container}
    >
      <TextInput
        onChange={(event) => handleQueryChange((event.target as HTMLInputElement).value)}
        value={query}
        disabled={props.disabled}
        label={props.label}
      />
      {isShowingOptions && !props.disabled && renderOptions()}
    </FocusAware>
  );
};
